import React from 'react'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined'
import { makeStyles } from '@material-ui/core/styles'
import CustomAvailabilityDestroyDialog from '../custom-availability/custom-availability-destroy-dialog'
import { formatOverrideDate, formatTimeRange } from './utils'

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#FFF8E1',
    border: '1px solid #FFE082',
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(1, 1.25),
      gap: theme.spacing(1),
    },
  },
  chip: {
    backgroundColor: '#FFE082',
    color: '#5D4037',
    fontWeight: 500,
    height: 26,
    borderRadius: 13,
  },
  chipIcon: {
    color: '#5D4037',
    marginLeft: theme.spacing(0.5),
  },
  textCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  date: {
    fontWeight: 500,
    lineHeight: 1.3,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(5, 2),
    [theme.breakpoints.only('xs')]: {
      padding: theme.spacing(4, 2),
    },
  },
  emptyIcon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 117, 202, 0.08)',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  emptyCaption: {
    color: 'rgba(0, 0, 0, 0.64)',
    maxWidth: 480,
    marginBottom: theme.spacing(2),
  },
}))

const OverridesList = ({ overrides, onChanged, emptyAction }) => {
  const classes = useStyles()
  if (!overrides || overrides.length === 0) {
    return (
      <Box className={classes.empty}>
        <Box className={classes.emptyIcon}>
          <EventAvailableOutlinedIcon style={{ fontSize: 32 }} />
        </Box>
        <Typography variant="h6" className={classes.emptyTitle}>
          No One-Time Availability
        </Typography>
        <Typography variant="body2" className={classes.emptyCaption}>
          Add extra hours on a specific day when you're available outside of
          your recurring weekly schedule.
        </Typography>
        {emptyAction}
      </Box>
    )
  }
  return (
    <Box>
      {overrides.map(override => {
        const sortedSlots = [...(override.timeSlots || [])].sort((a, b) =>
          a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0,
        )
        const timeRanges = sortedSlots
          .map(ts => formatTimeRange(ts.startTime, ts.endTime))
          .join(', ')
        const facility = override.coachFacility?.name
        return (
          <Card key={override.id} className={classes.card} variant="outlined">
            <Chip
              size="small"
              label="Open"
              className={classes.chip}
              icon={
                <ErrorOutlineIcon
                  className={classes.chipIcon}
                  style={{ fontSize: 14 }}
                />
              }
            />
            <Box className={classes.textCol}>
              <Typography className={classes.date}>
                {formatOverrideDate(override.date)}
              </Typography>
              <Typography variant="body2" className={classes.meta}>
                {[timeRanges, facility].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Box className={classes.actions}>
              <CustomAvailabilityDestroyDialog
                override={override}
                onSuccess={onChanged}
                trigger={
                  <IconButton size="small" aria-label="delete override">
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                }
              />
            </Box>
          </Card>
        )
      })}
    </Box>
  )
}

export default OverridesList
